import React, { useEffect, useRef } from 'react';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';

import FormikButton from '../../common/formik/FormikButton';
import FormikGrid from '../../common/formik/FormikGrid';
import FormikTextField from '../../common/formik/FormikTextField';
import { useUpdateFile } from './Document.hook';
import { FileDto } from './Document.interface';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';

interface RenameDialogForm {
  name: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required.')
});

export interface RenameDialogProps {
  file: FileDto;
  open: boolean;
  onCancel: () => void;
  onSuccess: (file: FileDto) => void;
  onError?: (error: any, errorMessage: string) => void;
}

const DocumentRenameDialog: React.FC<RenameDialogProps> = ({
  file,
  open,
  onCancel,
  onSuccess,
  onError
}) => {
  const formikRef = useRef<FormikValues>();
  const { isFolder } = file;
  const { isFieldDisabled } = useTaskEntityFields(file.task, false);
  const [{ data: renamedFile, isLoading, error }, renameFile] = useUpdateFile(
    file.id,
    isFolder
  );

  const fileLabel = isFolder ? 'folder' : 'file';
  const fileLabelCaps = isFolder ? 'Folder' : 'File';

  useEffect(() => {
    if (renamedFile) {
      onSuccess(renamedFile);
    }
  }, [renamedFile, onSuccess]);

  useEffect(() => {
    if (error) {
      console.log('Rename Error', error);
      onError?.(
        error,
        `Error renaming ${fileLabel} ${file.name}, ${error.message}`
      );
    }
  }, [error, file.name, fileLabel, onError]);

  useEffect(() => {
    if (!isLoading) {
      if (formikRef && formikRef.current) {
        formikRef.current.setSubmitting(false);
      }
    }
  }, [isLoading, file]);

  const handleSave = (data: RenameDialogForm) => renameFile(data);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={'xs'}
      aria-labelledby='form-dialog-title'
    >
      <Formik
        innerRef={(formik: any) => (formikRef.current = formik)}
        initialValues={{ name: file.name } as RenameDialogForm}
        onSubmit={handleSave}
        {...{ validationSchema }}
      >
        <Form autoComplete='off'>
          <DialogTitle id='form-dialog-title'>
            Rename {fileLabelCaps}
          </DialogTitle>
          <DialogContent>
            <FormikGrid>
              <FormikTextField
                autoFocus
                type='text'
                name='name'
                label={`${fileLabelCaps} Name`}
                disabled={!isFolder && isFieldDisabled('name')}
              />
            </FormikGrid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
            <FormikButton>Rename</FormikButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default DocumentRenameDialog;
